import type { ColumnDef, SortingState, PaginationState, RowSelectionState } from '@tanstack/react-table';
import * as React from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import type { FilterOptionChange, FilterState } from '../components/action-bar/filter-menu';
import useFilters from '../components/action-bar/hooks/use-filters';
import { createColumns, type DataGridColumnCreateReturn } from '../components/columns';
import {
	DEFAULT_META,
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
	DEFAULT_SORT_COLUMN,
	FETCH_DEBOUNCE_MS,
} from '../data-grid.constants';
import { OrderByEnum } from '../data-grid.type';
import type {
	DataGridBaseRow,
	DataGridColumnDef,
	DataGridOptions,
	DataGridProps,
	DataGridFilterFetcherResponse,
	SortTable,
} from '../data-grid.type';
import { getActionableItemCount } from '../util/rows';
import useApi, { type APIMeta } from './use-api';
import useDebounce from './use-debounce';
import useDebouncedLoading from './use-debounced-loading';

/**
 * List state supplied from outside the grid at mount, such as a URL-sync link. Omitted fields
 * fall back to the grid defaults.
 */
export interface IDataGridExternalState {
	activeFilters?: Record<string, FilterState>;
	hiddenColumnKeys?: string[];
	pageIndex?: number;
	searchQuery?: string;
	sortColumn?: string;
	sortDirection?: OrderByEnum;
	dateFrom?: string;
	dateTo?: string;
}

interface IDataGridHookProps<T extends DataGridBaseRow> {
	apiArgs?: DataGridProps<T>['apiArgs'];
	apiMethod?: DataGridProps<T>['apiMethod'];
	apiResponseParser?: DataGridProps<T>['apiResponseParser'];
	columns: DataGridColumnDef<T>[];
	data?: T[];
	defaultSortColumn?: string;
	defaultSortDirection?: OrderByEnum;
	hasActionColumn?: boolean;
	hasSelectionColumn?: boolean;
	initialSelectedRowIds?: DataGridProps<T>['initialSelectedRowIds'];
	initialState?: IDataGridExternalState;
	meta?: APIMeta;
	options: DataGridOptions;
	pageIndex?: number;
	pageSize?: number | null;
	uniqueField?: keyof T;
	filtersFetcher?: () => Promise<DataGridFilterFetcherResponse>;
}

const getInitialHiddenColumns = <T extends DataGridBaseRow>(columns: DataGridColumnDef<T>[]): Set<string> =>
	new Set(columns.filter(column => column.hidden === true).map(column => column.key));

export interface DataGridState<T> {
	activeFilters: Record<string, FilterState>;
	columns: ColumnDef<T>[];
	data: T[];
	error: Error | null;
	hiddenColumns: Set<string>;
	isAllPagesSelected: boolean;
	isLoading: boolean;
	pagination: PaginationState;
	searchQuery: string;
	selectedRows: Record<string, RowSelectionState>;
	selectedRowCount: number;
	sorting: SortingState;
}

export function useDataGrid<T extends DataGridBaseRow>({
	apiArgs = [],
	apiMethod,
	apiResponseParser,
	columns: tableColumns,
	data: tableData,
	defaultSortColumn,
	defaultSortDirection,
	hasActionColumn,
	hasSelectionColumn,
	initialSelectedRowIds,
	initialState,
	meta: outsideMeta = DEFAULT_META,
	options,
	pageIndex = DEFAULT_PAGE_INDEX,
	pageSize,
	uniqueField = 'id' as keyof T,
	filtersFetcher,
}: IDataGridHookProps<T>) {
	const { fetchData, clearCache } = useApi();

	// `pageSize` is `null` until the grid measures its real layout. A default is used only to seed
	// pagination for display; the actual fetch waits until pagination matches the measured size.
	const resolvedPageSize = pageSize ?? DEFAULT_PAGE_SIZE;

	const [activeFilters, setActiveFilters] = React.useState<Record<string, FilterState>>(
		initialState?.activeFilters ?? {}
	);
	const [hiddenColumns, setHiddenColumns] = React.useState<Set<string>>(() =>
		// An empty array is a real URL state ("every column visible"), so presence decides whether
		// the URL seeds the set, not truthiness.
		initialState?.hiddenColumnKeys != null
			? new Set(initialState.hiddenColumnKeys)
			: getInitialHiddenColumns(tableColumns)
	);
	const [data, setData] = React.useState<T[]>(tableData || []);
	const [error, setError] = React.useState<Error | null>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isDynamicFiltersInitialized, setIsDynamicFiltersInitialized] = React.useState(!filtersFetcher);
	const [isInitialized, setIsInitialized] = React.useState(!apiMethod);
	const isDebouncedLoading = useDebouncedLoading(isLoading);
	const [meta, setMeta] = React.useState<APIMeta>(outsideMeta);
	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex: initialState?.pageIndex ?? pageIndex,
		pageSize: resolvedPageSize,
	});
	const [searchQuery, setSearchQuery] = React.useState<string>(initialState?.searchQuery ?? '');
	const [selectedRows, setSelectedRows] = React.useState<Record<string, RowSelectionState>>({});
	const [isAllPagesSelected, setIsAllPagesSelected] = React.useState(false);
	const hasSeededInitialSelectionRef = React.useRef(false);

	React.useEffect(() => {
		if (hasSeededInitialSelectionRef.current) {
			return;
		}
		if (!initialSelectedRowIds?.length || data.length === 0) {
			return;
		}
		const presentRowIds = new Set(data.map(row => String(row[uniqueField])));
		const validSelectedIds = initialSelectedRowIds.filter(id => presentRowIds.has(String(id)));
		if (validSelectedIds.length === 0) {
			return;
		}
		const pageKey = String(pageIndex);
		const seeded: RowSelectionState = {};
		for (const id of validSelectedIds) {
			seeded[String(id)] = true;
		}
		setSelectedRows({ [pageKey]: seeded });
		hasSeededInitialSelectionRef.current = true;
	}, [data, initialSelectedRowIds, pageIndex, uniqueField]);
	const [dynamicFiltersOptions, setDynamicFiltersOptions] = React.useState<DataGridFilterFetcherResponse | null>(null);

	const actionableItemCount = getActionableItemCount(meta);

	// Calculate the number of selected rows from the actual data
	const selectedRowCount = React.useMemo(() => {
		if (isAllPagesSelected) {
			const excludedCount = Object.values(selectedRows).reduce(
				(acc, page) => acc + Object.values(page).filter(v => !v).length,
				0
			);
			return Math.max(0, actionableItemCount - excludedCount);
		}

		return Object.values(selectedRows).reduce((acc, page) => acc + Object.values(page).filter(v => v).length, 0);
	}, [actionableItemCount, isAllPagesSelected, selectedRows]);
	const initialSortColumn = initialState?.sortColumn ?? defaultSortColumn ?? DEFAULT_SORT_COLUMN;
	const initialSortDirection = initialState?.sortDirection ?? defaultSortDirection ?? OrderByEnum.DESC;
	const [sorting, setSorting] = React.useState<SortingState>([
		// Tanstack Table
		{
			id: initialSortColumn,
			desc: initialSortDirection === OrderByEnum.DESC,
		},
	]);
	const [sortValues, setSortValues] = React.useState<SortTable<string>>({
		// Bond API
		order: initialSortDirection,
		orderBy: initialSortColumn,
	});

	/**
	 * Seeded true whenever filter state arrives from outside, so the default-filter pass cannot
	 * overwrite the selection the user is already looking at. An outside source that reports no
	 * selected filters is a deliberate "nothing is filtered" view — a link that cleared a default
	 * filter, say — so it counts too and must not be refilled with defaults.
	 */
	const hasUserChangedFilters = React.useRef(initialState?.activeFilters != null);

	const { filterableColumns, visibleColumns } = React.useMemo(() => {
		const { filterableColumns, realColumns } = createColumns(tableColumns, {
			hasActionColumn,
			hasSelectionColumn,
			options,
		}) as unknown as DataGridColumnCreateReturn<T>;

		const visibleColumns = realColumns.filter((col: ColumnDef<T>) => {
			// @ts-expect-error Use accessorKey or id
			const columnKey = (col.accessorKey as string) || col.id;

			return !hiddenColumns.has(columnKey!);
		});

		return { filterableColumns, visibleColumns };
		// Depends on the set itself, not its size: restoring a view can swap which columns are
		// hidden without changing how many are.
	}, [hiddenColumns, options, tableColumns]);

	const handleClearSelections = React.useCallback(() => {
		setSelectedRows({});
		setIsAllPagesSelected(false);
	}, []);

	const handleResetPagination = React.useCallback(() => {
		setPagination(prev => ({
			...prev,
			pageIndex: DEFAULT_PAGE_INDEX,
		}));
	}, []);

	const dynamicFiltersRequestedRef = React.useRef(false);

	// During render, not in an effect: the render that carries the new args must already be on
	// page 1, so a URL commit or a fetch in that render cannot pair the new args with the old page.
	// The first render compares equal to itself, so a page restored at mount is kept.
	const [prevApiArgs, setPrevApiArgs] = React.useState(apiArgs);
	if (!areApiArgsEqual(prevApiArgs, apiArgs)) {
		setPrevApiArgs(apiArgs);
		setPagination(prev =>
			prev.pageIndex === DEFAULT_PAGE_INDEX ? prev : { ...prev, pageIndex: DEFAULT_PAGE_INDEX }
		);
	}

	const handleColumnVisibility = React.useCallback((columnKey: string, isVisible: boolean) => {
		setHiddenColumns(prev => {
			const newHidden = new Set(prev);

			if (isVisible) {
				newHidden.delete(columnKey);
			} else {
				newHidden.add(columnKey);
			}

			return newHidden;
		});
	}, []);

	const handleFiltersChange = React.useCallback(
		(update: FilterOptionChange[] | FilterOptionChange, isInitial = false) => {
			// if the filters are being initialized but the user has already changed some filters,
			// we don't want to override the user's changes
			if (isInitial && hasUserChangedFilters.current) {
				return;
			} else if (!isInitial && !hasUserChangedFilters.current) {
				// if the filters are being changed by the user, mark that the user has changed filters
				hasUserChangedFilters.current = true;
			}

			const newFilters = isInitial ? {} : { ...activeFilters };
			const updates = Array.isArray(update) ? update : [update];

			updates.forEach(({ groupKey, isSelected, optionKey, label }) => {
				if (!newFilters[groupKey]) {
					const col = tableColumns.find(c => c.key === groupKey);
					newFilters[groupKey] = {
						labels: new Map(),
						paramName: col?.filter?.paramName || groupKey,
						values: new Set(),
					};
				}

				if (isSelected) {
					newFilters[groupKey].labels.set(optionKey, label);
					newFilters[groupKey].values.add(optionKey);
				} else {
					newFilters[groupKey].labels.delete(optionKey);
					newFilters[groupKey].values.delete(optionKey);
				}

				// Remove the group if no options remain.
				if (newFilters[groupKey].values.size === 0) {
					delete newFilters[groupKey];
				}
			});

			setActiveFilters(newFilters);
			handleResetPagination();
			return newFilters;
		},
		[handleResetPagination, activeFilters]
	);

	const handleClearFilters = React.useCallback(() => {
		// an explicit clear is a user choice; initial (default) filters must not be re-applied after it
		hasUserChangedFilters.current = true;
		setActiveFilters({});
		handleResetPagination();
	}, [handleResetPagination]);

	const handleHideColumn = React.useCallback((columnKey: string) => {
		setHiddenColumns((prev: Set<string>) => new Set(prev).add(columnKey));
	}, []);

	const handleSearch = React.useCallback(
		(query: string) => {
			unstable_batchedUpdates(() => {
				setSearchQuery(query);
				handleResetPagination();
			});
		},
		[handleResetPagination]
	);

	/**
	 * Handles the selection of an individual row
	 * @param rowId - The ID of the row being selected/deselected
	 * @param isSelected - Whether the row should be selected (true) or deselected (false)
	 */
	const handleSelectRow = React.useCallback(
		(rowId: string | number, isSelected: boolean) => {
			setSelectedRows(prev => {
				const currentPageIndex = pagination.pageIndex.toString();
				const currentPageSelections = { ...(prev[currentPageIndex] || {}) };

				// If all pages are selected and we're deselecting a row
				if (isAllPagesSelected && !isSelected) {
					// Mark this row as explicitly excluded
					currentPageSelections[rowId] = false;
				}
				// If all pages are not selected or we're selecting a row
				else if (isSelected) {
					// Mark this row as explicitly selected
					currentPageSelections[rowId] = true;
				} else {
					// Remove this row from selections
					delete currentPageSelections[rowId];
				}

				// Return the updated selection state
				const updatedState = {
					...prev,
					[currentPageIndex]: currentPageSelections,
				};

				return updatedState;
			});
		},
		[isAllPagesSelected, pagination.pageIndex]
	);

	/**
	 * Handles the selection/deselection of all rows
	 * @param isSelected - Whether all rows should be selected (true) or deselected (false)
	 */
	const handleSelectAllRows = React.useCallback(
		(isSelected: boolean) => {
			if (isSelected) {
				// Clear any existing "excluded" selections on the current page
				setSelectedRows(prev => {
					const currentPageIndex = pagination.pageIndex.toString();
					return {
						...prev,
						[currentPageIndex]: {}, // Clear exclusions on current page
					};
				});

				// Set all pages as selected
				setIsAllPagesSelected(true);
			} else {
				// If we're deselecting all rows
				setSelectedRows({});
				setIsAllPagesSelected(false);
			}
		},
		[pagination.pageIndex]
	);

	const handleSetSorting = React.useCallback((newSorting: SortingState) => {
		const { id, desc } = newSorting[0];

		setSorting(newSorting);
		setSortValues({ orderBy: id, order: desc ? OrderByEnum.DESC : OrderByEnum.ASC });
	}, []);

	const handleSortAsc = React.useCallback((columnKey: string) => {
		setSortValues({ orderBy: columnKey, order: OrderByEnum.ASC });
		setSorting([{ id: columnKey, desc: false }]);
	}, []);

	const handleSortDesc = React.useCallback((columnKey: string) => {
		setSortValues({ orderBy: columnKey, order: OrderByEnum.DESC });
		setSorting([{ id: columnKey, desc: true }]);
	}, []);

	const { filterStates, refreshColumns, areFiltersInitialized } = useFilters({
		columns: filterableColumns,
		data,
		apiArgs,
		dynamicFiltersOptions,
		handleFiltersChange,
		isDynamicFiltersInitialized,
		selectedFilters: activeFilters,
	});

	const fetchGridData = React.useCallback(
		async (options: { forceRefresh?: boolean; pageIndex?: number } = {}) => {
			// Dynamic filter options only feed the filter menu, so they load in parallel with the
			// rows instead of serializing two round-trips (kicked off before the page-size gate so
			// the request is in flight during layout measurement).
			if (filtersFetcher && (!dynamicFiltersRequestedRef.current || options.forceRefresh)) {
				dynamicFiltersRequestedRef.current = true;
				filtersFetcher()
					.then(setDynamicFiltersOptions)
					.catch(e => console.error('Error fetching dynamic filter options:', e))
					.finally(() => setIsDynamicFiltersInitialized(true));
			}

			// Wait for an accurate, measured page size before fetching. Until the measured size has
			// been synced into pagination, skip the fetch so the placeholder size is never requested
			// (which is what caused the row count to shift right after the first render).
			if (pageSize == null || pagination.pageSize !== pageSize) {
				return;
			}

			// fetch filters only if table is not initialized or forceRefresh is true
			if (apiMethod) {
				setIsLoading(true);
				setError(null);
				// A superseded request resolves undefined from fetchData; the newer request owns the
				// loading/initialized state, so this invocation must not flip them off in its finally.
				let superseded = false;

				try {
					// Filters not ready: the initialize effect refires this fetch, which then owns the
					// loading/initialized flags - flipping them here would mark an empty grid initialized
					if (!areFiltersInitialized) {
						superseded = true;
						return;
					}

					const filterParams: Record<string, any> = {};

					try {
						Object.entries(activeFilters).forEach(([key, filter]) => {
							const paramKey = filter.paramName || key;
							filterParams[paramKey] = Array.from(filter.values);
						});
					} catch (e) {
						console.error('Error parsing active filters:', e);
					}

					const dataResponse = await fetchData({
						args: apiArgs,
						data: {
							itemsPerPage: pagination.pageSize,
							page: (options.pageIndex ?? pagination.pageIndex) + 1, // API pages are 1-based
							search: searchQuery,
							...sortValues,
							...filterParams,
						},
						method: apiMethod,
						forceRefresh: options.forceRefresh,
					});

					if (!dataResponse) {
						superseded = true;
						return;
					}

					const responseParser = apiResponseParser || apiDefaultResponseParser;
					setData(responseParser(dataResponse));

					if (dataResponse.meta) {
						setMeta(dataResponse.meta);
					}
				} catch (e) {
					const error = e instanceof Error ? e : new Error('An unknown error occurred');
					setError(error);
					throw error;
				} finally {
					if (!superseded) {
						setIsLoading(false);

						if (!isInitialized) {
							setIsInitialized(true);
						}
					}
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[
			activeFilters,
			// eslint-disable-next-line react-hooks/exhaustive-deps
			...apiArgs,
			apiMethod,
			apiResponseParser,
			fetchData,
			pageSize,
			pagination.pageIndex,
			pagination.pageSize,
			searchQuery,
			sortValues,
			filtersFetcher,
			isInitialized,
			areFiltersInitialized,
		]
	);

	React.useEffect(() => {
		if (!filterStates) return;

		setHiddenColumns(prev => {
			const hidden = new Set(prev);

			tableColumns.forEach(col => {
				if (typeof col.hidden === 'function') {
					if (col.hidden(filterStates, activeFilters)) {
						hidden.add(col.key);
					} else {
						hidden.delete(col.key);
					}
				}
			});

			return hidden;
		});
	}, [filterStates, activeFilters, tableColumns]);

	const refetch = React.useCallback(async (): Promise<void> => {
		handleClearSelections();

		clearCache();
		refreshColumns();

		// Stay on the page the user was editing. Resetting here is what sent a save on page 4
		// back to page 1.
		await fetchGridData({ forceRefresh: true, pageIndex: pagination.pageIndex });
	}, [fetchGridData, handleClearSelections, clearCache, refreshColumns, pagination.pageIndex]);

	const handleSetPagination = React.useCallback(
		(newPagination: PaginationState) => {
			setPagination(newPagination);
			// Force a refetch when pageSize changes
			if (newPagination.pageSize !== pagination.pageSize) {
				refetch();
			}
		},
		[pagination.pageSize, refetch]
	);

	/**
	 * Syncs the measured page size into pagination without moving the user off their page.
	 *
	 * The toolbar settling, or the grid remounting when they return from an edit, often changes
	 * the count by a row. Treating that as a new view reset a restored page 4 back to page 1.
	 */
	const applyMeasuredPageSize = React.useCallback((measuredSize: number) => {
		setPagination(prev => (prev.pageSize === measuredSize ? prev : { ...prev, pageSize: measuredSize }));
	}, []);

	const debouncedFetchData = useDebounce(fetchGridData, FETCH_DEBOUNCE_MS);

	// Effect to fetch data when filters, search, sorting, or pagination change
	React.useEffect(() => {
		if (apiMethod) {
			debouncedFetchData();
		}
	}, [
		activeFilters,
		apiMethod,
		debouncedFetchData,
		pagination.pageIndex,
		pagination.pageSize,
		searchQuery,
		sortValues.order,
		sortValues.orderBy,
	]);

	return {
		activeFilters,
		columns: visibleColumns,
		data,
		error,
		filterableColumns,
		hiddenColumns,
		isAllPagesSelected,
		isLoading: isDebouncedLoading,
		isInitialized,
		meta,
		pagination,
		searchQuery,
		selectedRows,
		selectedRowCount,
		sorting,
		sortValues,
		applyMeasuredPageSize,
		handleClearSelections,
		handleColumnVisibility,
		handleFiltersChange,
		handleClearFilters,
		handleHideColumn,
		handleResetPagination,
		handleSearch,
		handleSelectRow,
		handleSelectAllRows,
		handleSetPagination,
		handleSetSorting,
		handleSortAsc,
		handleSortDesc,
		refetch,
		setHiddenColumns,
		setIsAllPagesSelected,
		filterStates,
	};
}

function apiDefaultResponseParser<T>(response: { data: T[] }) {
	return response.data;
}

/**
 * Element-wise `Object.is`, matching the comparison the previous effect dependency list applied.
 * A new array with the same args is not a change; a new object inside it is.
 */
function areApiArgsEqual(previous: unknown[], next: unknown[]): boolean {
	if (previous.length !== next.length) {
		return false;
	}
	return previous.every((arg, index) => Object.is(arg, next[index]));
}
